import { GridColDef } from '@mui/x-data-grid-pro'
import Button from 'ext-ui/components/Button/Button'
import { PillProps } from 'ext-ui/components/Pill/Pill'
import React from 'react'
import * as style from 'src/app/CardFeeds/components/CardFeedsTable.style'
import mastercardLogoUrl from 'src/assets/images/issuerLogos/mastercard.svg'
import visaLogoUrl from 'src/assets/images/issuerLogos/visa.svg'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { dateFormatString } from 'src/localization/Dte'
import {
  CardFeedResponse,
  CardFeedStatus,
  CardNetwork,
} from 'src/queries/cardFeeds/cardFeeds.models'

// Brand names — rendered as data, not localized.
const NETWORK_LABEL: Record<CardNetwork, string> = {
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  AMERICAN_EXPRESS: 'American Express',
}

const NETWORK_LOGO: Partial<Record<CardNetwork, string>> = {
  VISA: visaLogoUrl,
  MASTERCARD: mastercardLogoUrl,
}

// Status copy is localized; keys exist in en-US.json.
const STATUS_LABEL: Record<CardFeedStatus, string> = {
  ACTIVE: 'Connected',
  SUBMITTED: 'Pending',
  DEACTIVATED: 'Disconnected',
  FAILED: 'Connection Failed',
}

const STATUS_PILL_COLOR: Record<CardFeedStatus, PillProps['color']> = {
  ACTIVE: 'highlight',
  SUBMITTED: 'pending',
  DEACTIVATED: undefined,
  FAILED: 'severe',
}

export interface BuildCardFeedsColumnsArgs {
  translate: (str: string, expressions?: Record<string, string>) => string
  onSeeCards: (feedId: string) => void
}

export const buildCardFeedsColumns = ({
  translate,
  onSeeCards,
}: BuildCardFeedsColumnsArgs): GridColDef<CardFeedResponse>[] => [
  {
    field: 'cardProgramName',
    headerName: translate('Card Feed'),
    sortable: true,
    flex: 1,
    minWidth: 180,
    renderCell: (params): React.ReactElement => (
      <DataGridCell
        text={
          (params.row.cardProgramName || '').trim() ||
          (params.row.status === 'SUBMITTED' || params.row.status === 'FAILED'
            ? translate('Pending Feed')
            : '—')
        }
        bold
        params={params}
      />
    ),
  },
  {
    field: 'referenceId',
    headerName: translate('Reference ID'),
    sortable: false,
    flex: 1,
    minWidth: 140,
    renderCell: (params): React.ReactElement => (
      <DataGridCell text={params.row.referenceId ?? '—'} params={params} />
    ),
  },
  {
    field: 'cardNetwork',
    headerName: translate('Card Network'),
    sortable: true,
    flex: 1,
    minWidth: 160,
    renderCell: (params): React.ReactElement => (
      <DataGridCellWrapper params={params}>
        <span css={style.networkCell}>
          {NETWORK_LOGO[params.row.cardNetwork] && (
            <img
              src={NETWORK_LOGO[params.row.cardNetwork]}
              alt=""
              css={style.networkLogo}
              data-testid="card-feeds-network-logo"
            />
          )}
          {NETWORK_LABEL[params.row.cardNetwork] ?? params.row.cardNetwork}
        </span>
      </DataGridCellWrapper>
    ),
  },
  {
    field: 'dateConnected',
    headerName: translate('Date Connected'),
    sortable: true,
    minWidth: 150,
    renderCell: (params): React.ReactElement => (
      <DataGridCell
        text={dateFormatString(params.row.createdAt, 'LL')}
        params={params}
      />
    ),
  },
  {
    field: 'cardCount',
    headerName: translate('Number of Cards'),
    sortable: false,
    minWidth: 140,
    renderCell: (params): React.ReactElement => (
      <DataGridCell text={params.row.cardCount ?? '—'} params={params} />
    ),
  },
  {
    field: 'status',
    headerName: translate('Status'),
    sortable: false,
    minWidth: 130,
    renderCell: (params): React.ReactElement => (
      <PillCell params={params} color={STATUS_PILL_COLOR[params.row.status]}>
        {STATUS_LABEL[params.row.status]
          ? translate(STATUS_LABEL[params.row.status])
          : params.row.status}
      </PillCell>
    ),
  },
  {
    field: 'actions',
    headerName: translate('Actions'),
    sortable: false,
    disableColumnMenu: true,
    // Fixed width sized so "See Cards" (and the longer fr-CA "Voir les cartes")
    // fits on one line with the cell's standard 16px padding — left-aligned, it
    // would otherwise truncate.
    width: 170,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params): React.ReactElement => (
      <DataGridCellWrapper params={params}>
        <Button
          variant="text"
          onClick={(event): void => {
            // The row itself navigates to the same place; stop the bubble so a
            // See Cards click doesn't also trigger the row handler (double nav +
            // double analytics).
            event.stopPropagation()
            onSeeCards(params.row.id)
          }}
          data-testid="card-feeds-see-enrolled"
        >
          {translate('See Cards')}
          <SvgChevronRight viewBox="0 0 24 24" css={style.seeEnrolledChevron} />
        </Button>
      </DataGridCellWrapper>
    ),
  },
]
